import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { BED_CLASS } from '@his/shared';
import { Bed } from 'lucide-react';
import { post, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { humanize, money } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { orgReturnTo } from './org-types';
import { codeEntry, useBedCharges, useRefreshWards, useWardRecord, type BedCharge, type BedRow } from './ward-types';

export function BedPage() {
  const { bedId = '' } = useParams();
  const [params] = useSearchParams();
  const { can } = useAuth();
  const returnTo = safeReturnTo(params.get('returnTo'), orgReturnTo('wards'));
  const wardId = params.get('wardId') ?? '';
  const branchId = params.get('branchId') ?? '';
  const roomId = params.get('roomId') ?? '';
  const chargesReadable = can('charge.read');

  const ward = useWardRecord(wardId, branchId);
  const charges = useBedCharges(chargesReadable);
  const room = ward.data?.rooms.find((row) => (bedId ? row.beds.some((bed) => bed.id === bedId) : row.id === roomId)) ?? null;
  const bed = bedId ? room?.beds.find((row) => row.id === bedId) ?? null : null;
  const loaded = Boolean(ward.data && room && (!bedId || bed));

  if (!loaded) {
    return (
      <FormScreen title={bedId ? tr('admin', 'loading') : tr('admin', 'add_bed')} icon={Bed} backTo={returnTo}>
        <RecordState error={ward.error} missing={!wardId || (ward.data !== undefined && !loaded)} />
      </FormScreen>
    );
  }

  return (
    <BedForm
      key={bedId || roomId}
      bed={bed}
      roomId={room?.id ?? roomId}
      roomCode={room?.code ?? ''}
      defaultBedClass={bed?.bedClass ?? ward.data?.defaultBedClass ?? 'general'}
      charges={charges.data ?? []}
      chargesReadable={chargesReadable}
      returnTo={returnTo}
    />
  );
}

function BedForm({
  bed,
  roomId,
  roomCode,
  defaultBedClass,
  charges,
  chargesReadable,
  returnTo,
}: {
  bed: BedRow | null;
  roomId: string;
  roomCode: string;
  defaultBedClass: string;
  charges: BedCharge[];
  chargesReadable: boolean;
  returnTo: string;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const refresh = useRefreshWards();
  const [initial] = useState(() => ({
    code: bed?.code ?? '',
    bedClass: bed?.bedClass ?? defaultBedClass,
    chargeItemId: bed?.chargeItemId ?? '',
  }));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const keptCharge = bed?.chargeItemId && !charges.some((charge) => charge.id === bed.chargeItemId) ? bed.chargeItemId : null;

  const save = useMutation({
    mutationFn: () => {
      const payload = { code: form.code, bedClass: form.bedClass, chargeItemId: form.chargeItemId || null };
      return bed ? put(`/ipd/beds/${bed.id}`, payload) : post('/ipd/beds', { roomId, ...payload });
    },
    onSuccess: () => {
      toast.success(bed ? tr('admin', 'bed_updated') : tr('admin', 'bed_created'));
      refresh();
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = form.code.length >= 1;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={bed ? tr('admin', 'edit_bed_x', { code: bed.code }) : tr('admin', 'add_a_bed_to_room_x', { code: roomCode })}
      description={tr('admin', 'the_daily_bed_charge_is_posted_to_every')}
      icon={Bed}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('admin', 'bed')} icon={Bed}>
        <Field label={tr('admin', 'code')} required hint={tr('admin', 'short_code_unique_within_the_room')}>
          <Input value={form.code} maxLength={16} onChange={(e) => setForm({ ...form, code: codeEntry(e.target.value) })} />
        </Field>
        <Field label={tr('admin', 'bed_class')} required>
          <Select value={form.bedClass} onChange={(e) => setForm({ ...form, bedClass: e.target.value })}>
            {BED_CLASS.map((value) => (
              <option key={value} value={value}>
                {humanize(value, 'bed_class')}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={tr('admin', 'daily_charge')}
          className="sm:col-span-2"
          hint={chargesReadable && charges.length === 0 ? tr('admin', 'no_bed_charges_yet_add_one_under_services') : undefined}
        >
          <Select
            value={form.chargeItemId}
            disabled={!chargesReadable}
            onChange={(e) => setForm({ ...form, chargeItemId: e.target.value })}
          >
            <option value="">{tr('admin', 'no_daily_charge')}</option>
            {keptCharge && <option value={keptCharge}>{tr('admin', 'current_charge_outside_the_bed_category')}</option>}
            {charges.map((charge) => (
              <option key={charge.id} value={charge.id}>
                {charge.code} · {charge.name} · {money(charge.basePriceMinor)}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={bed ? tr('admin', 'save_changes') : tr('admin', 'create_bed')}
        loading={save.isPending}
        incomplete={!ready}
        cancelTo={returnTo}
      />
    </FormScreen>
  );
}
